import { useEffect, useState } from 'react';
import { ManagerOptions, Socket, io } from 'socket.io-client';
import { TARA_BASE_URL } from '../constants/url';
import { getHeaders } from '../utils/fetch';
import { getUserInfo } from '../utils/user';

const lastSeqKey = (conversationId: string) => `tara_v3_lastSeq_${conversationId}`;

export const readLastSeq = (conversationId: string): number | null => {
    try {
        const raw = localStorage.getItem(lastSeqKey(conversationId));
        if (raw == null) return null;
        const n = Number(raw);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
};

export const writeLastSeq = (conversationId: string, seq: number) => {
    try {
        const existing = readLastSeq(conversationId);
        if (existing != null && existing >= seq) return;
        localStorage.setItem(lastSeqKey(conversationId), String(seq));
    } catch {
        // localStorage may be unavailable in private mode; replay still works
        // server-side via deliveredAt:null fallback.
    }
};

function useSocketV3() {
    const [socket, setSocket] = useState<Socket>();

    const connect = (query: ManagerOptions['query'], conversationId?: string | null) =>
        new Promise<Socket>((resolve) => {
            getUserInfo().then((userInfo) => {
                const { hostname, pathname } = window.location;
                const subDomain =
                    hostname === 'bus.ixigo.com' || pathname.startsWith('/buses') ? 'abhibus' : hostname.split('.')[1];

                const lastSeq = conversationId ? readLastSeq(conversationId) : null;

                const socketInstance = io(`https://${subDomain}.elitedesk.in`, {
                    path: `${TARA_BASE_URL}/socket/chat-v3`,
                    transports: ['websocket', 'polling'],
                    query,
                    auth: {
                        ...getHeaders(),
                        cookie: document.cookie,
                        userid: userInfo?.userId,
                        lastSeq: lastSeq != null ? String(lastSeq) : '',
                    },
                    withCredentials: true,
                    reconnection: true,
                    reconnectionAttempts: Infinity,
                    reconnectionDelay: 500,
                    reconnectionDelayMax: 5000,
                });

                setSocket(socketInstance);

                socketInstance.on('connect_error', (err) => {
                    err.message = `Websocket connection error, opted for polling: ${err.message}`;
                    socketInstance.io.opts.transports = ['polling', 'websocket'];
                });

                resolve(socketInstance);
            });
        });

    useEffect(
        () => () => {
            socket?.close();
        },
        [],
    );

    return { connect };
}

export default useSocketV3;
